import Swiper from "swiper";
import { Pagination, Autoplay, EffectFade } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-fade";

Swiper.use([Pagination, Autoplay, EffectFade]);
window.Swiper = Swiper;

import Lenis from "lenis";
import { initParallax, updateParallax } from "./parallax.js";

const lenis = new Lenis();
window.lenis = lenis;
function raf(time) {
    lenis.raf(time);
    updateParallax();
    requestAnimationFrame(raf);
}
requestAnimationFrame(raf);

window.initParallax = initParallax;

document.addEventListener("DOMContentLoaded", () => {
    initParallax();

    const BASE_DELAY_MS = 100;
    const STAGGER_MS = 500;
    const IO_OPTIONS = { rootMargin: "0px 0px -8% 0px", threshold: 0 };

    const groupObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            [...entry.target.querySelectorAll("[data-animate]")]
                .sort(
                    (a, b) =>
                        parseInt(getComputedStyle(a).order, 10) -
                        parseInt(getComputedStyle(b).order, 10),
                )
                .forEach((el, i) => {
                    el.style.transitionDelay = `${BASE_DELAY_MS + i * STAGGER_MS}ms`;
                    el.classList.add("is-visible");
                });
            groupObserver.unobserve(entry.target);
        });
    }, IO_OPTIONS);

    const revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.style.transitionDelay = `${BASE_DELAY_MS}ms`;
            entry.target.classList.add("is-visible");
            revealObserver.unobserve(entry.target);
        });
    }, IO_OPTIONS);

    document
        .querySelectorAll("[data-animate-group]")
        .forEach((el) => groupObserver.observe(el));
    document.querySelectorAll("[data-animate]").forEach((el) => {
        if (!el.closest("[data-animate-group]")) revealObserver.observe(el);
    });
});

window.algoliasearch = (appId, apiKey) =>
    import("algoliasearch").then(({ algoliasearch }) =>
        algoliasearch(appId, apiKey),
    );

import { CountUp } from "countup.js";
import Alpine from "alpinejs";
import collapse from "@alpinejs/collapse";
import focus from "@alpinejs/focus";
import yachtIndex from "./yacht-index.js";
import textWithAssetVerticalSlider from "./text-with-asset-vertical-slider.js";

Alpine.plugin(collapse);
Alpine.plugin(focus);

Alpine.data("yachtIndex", yachtIndex);
Alpine.data("textWithAssetVerticalSlider", textWithAssetVerticalSlider);

Alpine.data("countUp", () => ({
    init() {
        const el = this.$el;
        const text = el.textContent.trim();
        const match = text.match(/^([^0-9]*)([0-9]+(?:\.[0-9]+)?)([^0-9]*)$/);
        if (!match) return;
        const [, prefix, numStr, suffix] = match;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;
                    new CountUp(el, parseFloat(numStr), {
                        prefix,
                        suffix,
                        duration: 2.5,
                        useEasing: true,
                    }).start();
                    observer.unobserve(el);
                });
            },
            { threshold: 0.5 },
        );
        observer.observe(el);
    },
}));

window.Alpine = Alpine;

Alpine.store("yachtLists", {
    enquiry: JSON.parse(localStorage.getItem("yachtEnquiry") || "[]"),
    toast: false,
    toastTimeout: null,

    inEnquiry(id) {
        return this.enquiry.some((i) => String(i.id) === String(id));
    },

    toggleEnquiry(item) {
        this.inEnquiry(item.id)
            ? this.removeEnquiry(item.id)
            : this.addEnquiry(item);
    },
    addEnquiry(item) {
        if (!this.inEnquiry(item.id)) {
            this.enquiry.push(item);
            localStorage.setItem("yachtEnquiry", JSON.stringify(this.enquiry));
            this.showToast();
        }
    },
    showToast() {
        this.toast = true;
        clearTimeout(this.toastTimeout);
        this.toastTimeout = setTimeout(() => {
            this.toast = false;
        }, 3000);
    },
    removeEnquiry(id) {
        this.enquiry = this.enquiry.filter((i) => String(i.id) !== String(id));
        localStorage.setItem("yachtEnquiry", JSON.stringify(this.enquiry));
    },
});

Alpine.store("yachtEnquiry", {
    open: false,
    yacht: null,
    type: "sales",

    show(yacht = null, type = "sales") {
        this.yacht = yacht;
        this.type = type;
        this.open = true;
    },
    hide() {
        this.open = false;
    },
});

const PIN_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="37" fill="none" viewBox="0 0 30 37"><path fill="#12223f" d="M14.666 16.63c1.519 0 2.75-1.24 2.75-2.771s-1.231-2.772-2.75-2.772-2.75 1.24-2.75 2.771 1.231 2.772 2.75 2.772"/><path fill="#12223f" d="M14.667 0a14.6 14.6 0 0 0-10.32 4.277A14.84 14.84 0 0 0 0 14.635C0 24.76 12.925 36.032 13.475 36.513a1.825 1.825 0 0 0 2.383 0c.642-.48 13.475-11.752 13.475-21.878a14.84 14.84 0 0 0-4.347-10.358A14.6 14.6 0 0 0 14.666 0m0 20.326c-1.27 0-2.51-.38-3.565-1.09a6.46 6.46 0 0 1-2.364-2.903 6.5 6.5 0 0 1-.365-3.736 6.5 6.5 0 0 1 1.756-3.312 6.4 6.4 0 0 1 3.286-1.77 6.37 6.37 0 0 1 3.707.368 6.43 6.43 0 0 1 2.88 2.382 6.5 6.5 0 0 1-.798 8.167 6.4 6.4 0 0 1-4.537 1.894"/></svg>`;

const PLACEHOLDER_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="none" viewBox="0 0 24 24"><path stroke="#12223f" stroke-opacity="0.3" stroke-width="1.5" d="M2 12c0-4.243 0-6.364 1.318-7.682S6.757 3 11 3h2c4.243 0 6.364 0 7.682 1.318S22 7.757 22 12s0 6.364-1.318 7.682S17.243 21 13 21h-2c-4.243 0-6.364 0-7.682-1.318S2 16.243 2 12Z"/><circle cx="16" cy="8" r="2" stroke="#12223f" stroke-opacity="0.3" stroke-width="1.5"/><path stroke="#12223f" stroke-opacity="0.3" stroke-width="1.5" stroke-linejoin="round" d="m2 13 3.293-3.293a1 1 0 0 1 1.414 0L12 15l2.293-2.293a1 1 0 0 1 1.414 0L19 16"/></svg>`;

Alpine.data("mapbox", ({ token, locations = [], options = {} }) => ({
    init() {
        const el = this.$el;

        const initMap = async () => {
            const [{ default: mapboxgl }, { default: Supercluster }] =
                await Promise.all([
                    import("mapbox-gl"),
                    import("supercluster"),
                ]);
            await import("mapbox-gl/dist/mapbox-gl.css");

            mapboxgl.accessToken = token;

            const map = new mapboxgl.Map({
                container: el,
                style: "mapbox://styles/rosiewatt/cmr27p9d6001m01r50gtcd5jt",
                projection: "mercator",
                zoom: 1.5,
                renderWorldCopies: false,
                trackResize: false,
                scrollZoom: false,
                ...options,
            });
            el._map = map;

            map.addControl(
                new mapboxgl.NavigationControl({ showCompass: false }),
                "top-right",
            );

            const ro = new ResizeObserver(() => {
                if (el.offsetWidth > 0 && el.offsetHeight > 0) map.resize();
            });
            ro.observe(el);

            // Supercluster index
            const cluster = new Supercluster({ radius: 60, maxZoom: 16 });
            cluster.load(
                locations.map((loc) => ({
                    type: "Feature",
                    geometry: {
                        type: "Point",
                        coordinates: [loc.lng, loc.lat],
                    },
                    properties: {
                        title: loc.title ?? "",
                        description: loc.description ?? "",
                        image: loc.image ?? "",
                        url: loc.url ?? "",
                    },
                })),
            );

            const activeMarkers = {};
            let activePopup = null;

            const popupHtml = ({ title, description, image, url }) => `
                ${
                    image
                        ? `<img src="${image}" style="width:100%;height:160px;object-fit:cover;display:block;" alt="">`
                        : `<div style="width:100%;height:160px;background:#e2e3e4;display:flex;align-items:center;justify-content:center;">${PLACEHOLDER_SVG}</div>`
                }
                <div style="padding:16px 20px;">
                    <p class="popup-title" style="margin:0 0 8px;font-size:22px;font-weight:700;letter-spacing:0.02em;text-transform:uppercase;text-align:center;color:#12223f;">${title}</p>
                    ${description ? `<p style="margin:0 0 16px;font-size:13px;line-height:1.6;color:#555;text-align:center;">${description}</p>` : ""}
                    ${url ? `<a href="${url}" class="popup-read-more" style="display:block;text-align:center;font-size:12px;letter-spacing:1.5px;text-transform:uppercase;color:#12223f;padding:8px 16px;text-decoration:none;">Read More</a>` : ""}
                </div>`;

            const updateMarkers = () => {
                const bounds = map.getBounds();
                const zoom = Math.floor(map.getZoom());
                const clusters = cluster.getClusters(
                    [
                        bounds.getWest(),
                        bounds.getSouth(),
                        bounds.getEast(),
                        bounds.getNorth(),
                    ],
                    zoom,
                );

                const seen = new Set();
                clusters.forEach((feature) => {
                    const id = String(
                        feature.id ?? `${feature.geometry.coordinates}`,
                    );
                    seen.add(id);
                    if (activeMarkers[id]) return;

                    const [lng, lat] = feature.geometry.coordinates;
                    const markerEl = document.createElement("div");

                    if (feature.properties.cluster) {
                        markerEl.innerHTML = `<div style="width:44px;height:44px;background:#12223f;border-radius:50%;border:2px solid #fff;display:flex;align-items:center;justify-content:center;color:#fff;font-size:14px;font-weight:600;cursor:pointer;">${feature.properties.point_count_abbreviated}</div>`;
                        markerEl.addEventListener("click", () => {
                            const expansionZoom = Math.min(
                                cluster.getClusterExpansionZoom(feature.id),
                                20,
                            );
                            map.flyTo({
                                center: [lng, lat],
                                zoom: expansionZoom,
                            });
                        });
                    } else {
                        markerEl.style.cursor = "pointer";
                        markerEl.innerHTML = PIN_SVG;

                        const popup = new mapboxgl.Popup({
                            maxWidth: "280px",
                            anchor: "bottom",
                            // Offsets are measured from the marker's lngLat, so this
                            // clears the full 37px pin height plus a gap.
                            offset: [0, -60],
                            closeButton: true,
                            closeOnClick: false,
                            focusAfterOpen: false,
                        }).setHTML(popupHtml(feature.properties));

                        markerEl.addEventListener("click", () => {
                            if (activePopup && activePopup !== popup)
                                activePopup.remove();
                            activePopup = popup;
                            map.flyTo({ center: [lng, lat], offset: [0, 170] });
                        });
                        popup.on("close", () => {
                            if (activePopup === popup) activePopup = null;
                        });

                        // The pin is a teardrop — its tip is the location, not its
                        // centre. Mapbox anchors to centre by default, which reads as
                        // correct when zoomed in but drifts by ~18px of map at low zoom.
                        activeMarkers[id] = new mapboxgl.Marker({
                            element: markerEl,
                            anchor: "bottom",
                        })
                            .setLngLat([lng, lat])
                            .setPopup(popup)
                            .addTo(map);
                        return;
                    }

                    activeMarkers[id] = new mapboxgl.Marker({
                        element: markerEl,
                    })
                        .setLngLat([lng, lat])
                        .addTo(map);
                });

                Object.keys(activeMarkers).forEach((id) => {
                    if (!seen.has(id)) {
                        activeMarkers[id].remove();
                        delete activeMarkers[id];
                    }
                });
            };

            map.on("load", () => {
                map.getCanvas().style.filter = "grayscale(1)";
                updateMarkers();
            });
            map.on("moveend", updateMarkers);
        };

        const io = new IntersectionObserver(
            (entries) => {
                if (!entries[0].isIntersecting) return;
                io.disconnect();
                initMap();
            },
            { threshold: 0.01 },
        );
        io.observe(el);
    },
}));

Alpine.start();
